import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { useContent } from "../context/ContentContext";
import { external } from "../lib/links";
import { DynamicIcon } from "../lib/dynamicIcon";
import { slugify } from "../lib/slug";

export const TreatmentAreas = () => {
  const { content } = useContent();
  const t = content.treatment;
  const links = content.links;

  return (
    <section id="treatment" data-testid="treatment-section" className="relative py-20 md:py-28 bg-cream-2/50">
      <div className="mx-auto max-w-6xl px-5 md:px-8">
        <div className="mb-14">
          <Reveal><p className="text-xs md:text-sm uppercase tracking-[0.24em] font-semibold text-ocean">{t.eyebrow}</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl tracking-tight text-ink leading-[1.05]">{t.heading}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-lg text-ink-soft leading-relaxed">{t.intro}</p></Reveal>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {(t.areas || []).filter((a) => a.active !== false).map((area, i) => {
            const slug = area.slug || slugify(area.name);
            return (
              <motion.div
                key={(area.id || slug) + i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/treatment-areas/${slug}`}
                  data-testid={`treatment-card-${i}`}
                  title={area.blurb || `Learn more about ${area.name}`}
                  className="group flex items-center gap-3 bg-white rounded-2xl border border-cream-2 px-4 py-4 md:px-5 md:py-4 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:border-sage/40 hover:shadow-[0_16px_32px_-20px_rgba(122,143,119,0.45)]"
                >
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sage/12 text-sage-dark transition-colors duration-300 group-hover:bg-sage group-hover:text-white">
                    <DynamicIcon name={area.icon} className="h-4 w-4" strokeWidth={1.7} faSize={16} />
                  </span>
                  <span className="font-medium text-[15px] md:text-base text-ink leading-tight">{area.name}</span>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {t.spotlightEnabled !== false && (t.neuroHeading || t.neuroBody) && (
          <Reveal delay={0.08}>
            <div className="mt-8 rounded-3xl bg-sage/10 border border-sage/25 p-8 md:p-10 flex flex-col md:flex-row gap-6 md:gap-8">
              <div className="md:w-1/4 shrink-0">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-sage text-white mb-4">
                  <DynamicIcon name={t.neuroIcon || "Puzzle"} className="h-5 w-5" strokeWidth={1.7} faSize={20} />
                </span>
                <h3 className="font-serif text-2xl text-ink leading-tight">{t.neuroHeading}</h3>
              </div>
              <p className="md:w-3/4 text-[15px] md:text-base text-ink-soft leading-relaxed">{t.neuroBody}</p>
            </div>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col sm:flex-row items-center gap-4">
            <a data-testid="treatment-book-btn" href={links.janeBook} {...external} className="inline-flex items-center gap-2 rounded-full bg-sage text-white px-8 py-4 font-semibold transition-[background-color,transform] duration-300 hover:bg-sage-dark hover:-translate-y-0.5">
              {t.ctaLabel} <ArrowUpRight className="h-4 w-4" />
            </a>
            <p className="text-ink-muted text-sm">{t.ctaNote}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default TreatmentAreas;
